import createClient, { type Middleware } from 'openapi-fetch'
import { API_ROOT } from 'config'
import { getLanguage } from 'store/locale/utils'
import { userManager } from 'scenes/authentication/oicd'
import type { paths } from './generated/office'
import type { paths as gatewayPaths } from './generated/core-gateway'
import type { Mutable } from './types'

/**
 * Typed API client. Path, method, params, request body and response type are all
 * checked against openapi/openapi.json — a removed endpoint or a renamed path param
 * is a compile error at the call site.
 *
 * Sends the same headers as utils/apiClient so the two can coexist while domains
 * migrate one at a time.
 */
const authMiddleware: Middleware = {
  async onRequest({ request }) {
    request.headers.set('Accept-Language', getLanguage())

    const user = await userManager.getUser()
    if (user) {
      request.headers.set('Authorization', `Bearer ${user.access_token}`)
      if (user.id_token) {
        request.headers.set('X-ID-Token', user.id_token)
      }
    }

    return request
  },
}

export const apiClient = createClient<paths>({ baseUrl: API_ROOT })
apiClient.use(authMiddleware)

/**
 * Rewrites core-gateway paths to the /api/2/ proxy equivalents.
 * e.g. /challenges-admin/v1/challenges/{id} → /api/2/challenges/{id}
 */
const gatewayProxyMiddleware: Middleware = {
  async onRequest({ request }) {
    const url = new URL(request.url)
    url.pathname = url.pathname.replace(/\/challenges-(?:admin|participant|media)\/v1/, '/api/2')
    return new Request(url, request)
  },
}

/**
 * Typed against the core-gateway spec. Uses the same auth middleware as apiClient
 * and rewrites gateway paths to /api/2/ proxy paths before sending.
 *
 * Use this for endpoints defined in the core-gateway spec but not yet declared
 * in the office spec — avoids `as any` casts while keeping full type safety.
 */
export const gatewayApiClient = createClient<gatewayPaths>({ baseUrl: API_ROOT })
gatewayApiClient.use(authMiddleware)
gatewayApiClient.use(gatewayProxyMiddleware)

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

interface ApiResult<T> {
  data?: T
  error?: unknown
  response: Response
}

/**
 * Turns openapi-fetch's `{ data, error }` result into a promise that throws, so
 * domain functions read like the axios helpers the rest of the app already uses
 * and createApiThunk's error handling keeps working unchanged.
 */
export const unwrap = async <T>(result: Promise<ApiResult<T>>): Promise<Mutable<T>> => {
  const { data, error, response } = await result

  if (error !== undefined || !response.ok) {
    throw new ApiError(errorMessage(error, response), response.status, error)
  }

  return data as Mutable<T>
}

/**
 * For the /api/2/* endpoints. They are thin proxies for the CoreV2 public gateway and
 * declare no response body of their own, so openapi-fetch can only infer `unknown` from
 * the office spec. Supply the response type from the gateway spec instead:
 *
 *   proxied<Challenge>(apiClient.GET('/api/2/challenges/{challengeId}', { ... }))
 *
 * The path, method and parameters are still checked against the office spec; only the
 * response body is asserted. Nothing verifies that assertion at runtime — see
 * "Where the spec lies" in the README before using it on a critical path.
 */
export const proxied = <T>(result: Promise<ApiResult<unknown>>): Promise<Mutable<T>> =>
  unwrap(result as Promise<ApiResult<T>>)

const errorMessage = (error: unknown, response: Response): string => {
  if (typeof error === 'string' && error) {
    return error
  }
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message
  }

  return `Request failed with status ${response.status}`
}
